import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import {Avatar} from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type {SxProps} from '@mui/system';
import {lighten} from '@mui/system/colorManipulator';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import InputText from '@/components/common/forms/InputText';
import Label from '@/components/common/Label';
import {secondaryColor} from '@/theme/theme';

export type Props = {
  loginErrorMessage?: string;
  username: string;
  password: string;
  isLoading?: boolean;
  onUsernameChanged: (name: string) => void;
  onPasswordChanged: (password: string) => void;
  onLoginButtonClicked: () => void;
};

const LoginForm: FC<Props> = ({
  loginErrorMessage,
  username,
  password,
  isLoading,
  onUsernameChanged,
  onPasswordChanged,
  onLoginButtonClicked,
}) => {
  const {t} = useTranslation();

  const handleNameChange = (name: string) => onUsernameChanged(name);
  const handlePasswordChange = (password: string) => onPasswordChanged(password);
  const handleLoginClick = (e: React.FormEvent) => {
    e.preventDefault();
    onLoginButtonClicked();
  };

  return (
    <Stack component="form" direction="column" sx={styles.container} onSubmit={handleLoginClick}>
      <Avatar sx={styles.avatar}>
        <LockIcon sx={{color: 'common.white'}} />
      </Avatar>
      <Label value={t('authentication.email')}>
        <InputText
          placeholder={t('authentication.email')}
          onChange={handleNameChange}
          startAdornment={<AccountCircleIcon sx={styles.startAdornment} />}
          required={true}
          size="medium"
        />
      </Label>
      <Label value={t('authentication.password')}>
        <InputText
          placeholder={t('authentication.password')}
          onChange={handlePasswordChange}
          startAdornment={<LockIcon sx={styles.startAdornment} />}
          required={true}
          type="password"
          size="medium"
        />
      </Label>
      <Typography variant="body2" sx={styles.errorText}>
        {loginErrorMessage ? loginErrorMessage : ''}
      </Typography>

      <Button type="submit" fullWidth variant="contained" sx={styles.loginButton} disabled={!username || !password}>
        {isLoading ? `${t('authentication.loggingIn')}` : t('authentication.login')}
      </Button>
    </Stack>
  );
};

export default LoginForm;

const styles: Record<string, SxProps> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    width: {xs: '90vw', sm: '70vw', md: '35vw'},
    margin: '1rem',
  },
  avatar: {
    bgcolor: 'primary.main',
  },
  textField: {
    mb: 1,
    bgcolor: lighten(secondaryColor, 0.95),
  },
  errorText: {
    color: 'error.main',
    mb: 2,
    ml: 2,
  },
  loginButton: {
    mb: 2,
    borderRadius: 0,
  },
  startAdornment: {
    color: 'primary.main',
    mr: 1,
  },
};
